import { AddCircleIcon, ButtonLink } from '@wonderschool/common-base-ui';
import { useCallback } from 'react';
import { UseFormReturn, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line no-restricted-imports
import { Button, Divider } from 'semantic-ui-react';
import { v4 as uuidv4 } from 'uuid';
import { FormComponentProps } from '../../../common';
import { DEFAULT_PARENT } from '../../../students/consts';
import { ParentType, StudentAndParentsType } from '../../../students/types';
import EnrollmentParentForm from './EnrollmentParentForm';

type EnrollmentParentsFormProps = FormComponentProps<ParentType | ParentType[]> & {
  parents: ParentType[];
  form: UseFormReturn<StudentAndParentsType>;
};

function EnrollmentParentsForm({ title, hasTitle = true, form, parents }: EnrollmentParentsFormProps) {
  const { t } = useTranslation();
  const { control } = form;
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'parents',
  });

  const viewTitle = hasTitle ? t(title || 'students.parentsFormTitle') : '';

  const addParent = useCallback(() => {
    append({ ...DEFAULT_PARENT, uuid: uuidv4() });
  }, [append]);

  const removeParent = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove]
  );

  return (
    <div className="parents-form">
      {!!viewTitle && (
        <h2 className="mb-4 text-2xl font-bold" data-testid="parent-form-title">
          {viewTitle}
        </h2>
      )}
      {fields.map((field, index) => (
        <div key={field.id}>
          <h3 data-testid="parent-form-title">
            {index > 0 && !parents[index]?.isInterestedFamily && (
              <Button
                basic
                className="ws-icon-button"
                floated="right"
                size="large"
                icon="trash alternate outline"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeParent(index);
                }}
              />
            )}

            {t('students.parentsFormTitle') + ' ' + (index + 1)}
          </h3>
          <EnrollmentParentForm parent={parents[index]} parents={parents} form={form} parentIndex={index} />
          <Divider />
        </div>
      ))}
      <div>
        <ButtonLink
          onClick={addParent}
          data-testid="add-another-parent-link"
          labelWrapperClasses="ring-gray-600 color-gray-600 text-base"
          extraClasses="color-gray-600 text-base"
        >
          <AddCircleIcon data-testid="add-another-parent-icon" className="size-6" />
          <span>{t('students.parentsAddParentLabel')}</span>
        </ButtonLink>
      </div>
    </div>
  );
}

export default EnrollmentParentsForm;
